import React from 'react';
import { CalendarDays, Compass, Target, Users } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Section, Field, AutoText, AutoInput, ListEditor } from './OnsiteFields';

export function MetaSection({ plan, setField }) {
  const m = plan.meta || {};
  const set = (k) => (v) => setField(`meta.${k}`, v, 'meta');
  return (
    <Section id="os-meta" icon={CalendarDays} title="Termin & Rahmen" meta={plan.section_meta?.meta} data={m} testId="onsite-meta">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Datum"><AutoInput type="date" value={m.date} onChange={set('date')} testId="onsite-meta-date" /></Field>
        <Field label="Beginn"><AutoInput type="time" value={m.start} onChange={set('start')} testId="onsite-meta-start" /></Field>
        <Field label="Ende"><AutoInput type="time" value={m.end} onChange={set('end')} testId="onsite-meta-end" /></Field>
        <Field label="Abendessen">
          <div className="flex items-center gap-2 h-8"><Switch checked={!!m.dinner} onCheckedChange={set('dinner')} data-testid="onsite-meta-dinner" /><span className="text-xs text-muted-foreground">{m.dinner ? 'geplant (unsere Einladung)' : 'nicht geplant'}</span></div>
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Ort / Betriebsstätte"><AutoInput value={m.location} onChange={set('location')} testId="onsite-meta-location" /></Field>
        <Field label="Adresse"><AutoInput value={m.address} onChange={set('address')} testId="onsite-meta-address" /></Field>
        <Field label="Teilnehmer Verkäuferseite"><AutoInput value={m.seller_participants} onChange={set('seller_participants')} testId="onsite-meta-seller" /></Field>
        <Field label="Teilnehmer unser Team"><AutoInput value={m.team_participants} onChange={set('team_participants')} testId="onsite-meta-team" /></Field>
        <Field label="Rahmen"><AutoInput value={m.frame} onChange={set('frame')} /></Field>
        <Field label="Zweck des Termins"><AutoInput value={m.purpose} onChange={set('purpose')} /></Field>
      </div>
    </Section>
  );
}

const BASELINE = [['project', 'Projekt & Target'], ['status', 'Stand im Prozess'], ['purpose', 'Zweck'], ['preparation', 'Vorbereitung'], ['nda_status', 'NDA-Status']];
const KNOWN = [['history', 'Historie'], ['numbers', 'Zahlen'], ['organisation', 'Organisation'], ['customers', 'Kunden'], ['sources', 'Quellen']];

export function BaselineSection({ plan, setField }) {
  return (
    <Section id="os-baseline" icon={Compass} title="Ausgangslage & was wir wissen" subtitle="interner Faktenstand — Basis für Hypothesen und Fragen"
      meta={plan.section_meta?.baseline || plan.section_meta?.known} data={{ b: plan.baseline, k: plan.known }} testId="onsite-baseline">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-wider text-teal-400/80">Ausgangslage</p>
          {BASELINE.map(([k, l]) => <Field key={k} label={l}><AutoText value={plan.baseline?.[k]} onChange={(v) => setField(`baseline.${k}`, v, 'baseline')} testId={`onsite-baseline-${k}`} /></Field>)}
        </div>
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-wider text-teal-400/80">Was wir wissen</p>
          {KNOWN.map(([k, l]) => <Field key={k} label={l}><AutoText value={plan.known?.[k]} onChange={(v) => setField(`known.${k}`, v, 'known')} testId={`onsite-known-${k}`} /></Field>)}
        </div>
      </div>
    </Section>
  );
}

export function GoalsSection({ plan, setField }) {
  const goals = plan.goals || [];
  const upd = (i, patch) => setField('goals', goals.map((g, j) => (j === i ? { ...g, ...patch } : g)), 'goals');
  return (
    <Section id="os-goals" icon={Target} title="Ziele des Termins" meta={plan.section_meta?.goals} data={goals} testId="onsite-goals">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {goals.map((g, i) => (
          <div key={i} className="border border-border/70 rounded-sm p-3 space-y-2 bg-secondary/10">
            <AutoInput value={g.title} onChange={(v) => upd(i, { title: v })} className="font-medium" testId={`onsite-goal-title-${i}`} />
            <ListEditor items={g.points || []} onChange={(v) => upd(i, { points: v })} testId={`onsite-goal-points-${i}`} />
          </div>
        ))}
      </div>
      <Field label="Erfolg — so sieht ein guter Termin aus"><AutoText value={plan.success} onChange={(v) => setField('success', v)} testId="onsite-success" /></Field>
    </Section>
  );
}

export function RolesSection({ plan, setField }) {
  const r = plan.roles || {};
  const set = (k) => (v) => setField(`roles.${k}`, v, 'roles');
  return (
    <Section id="os-roles" icon={Users} title="Rollen im Team" meta={plan.section_meta?.roles} data={r} testId="onsite-roles">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-border/70 rounded-sm p-3 space-y-2">
          <Field label="Lead A"><AutoInput value={r.lead_a} onChange={set('lead_a')} testId="onsite-roles-lead-a" /></Field>
          <Field label="Zuständig für"><AutoText value={r.lead_a_scope} onChange={set('lead_a_scope')} /></Field>
        </div>
        <div className="border border-border/70 rounded-sm p-3 space-y-2">
          <Field label="Lead B"><AutoInput value={r.lead_b} onChange={set('lead_b')} testId="onsite-roles-lead-b" /></Field>
          <Field label="Zuständig für"><AutoText value={r.lead_b_scope} onChange={set('lead_b_scope')} /></Field>
        </div>
        <Field label="Gemeinsam"><AutoText value={r.joint} onChange={set('joint')} /></Field>
        <Field label="Hinweise"><AutoText value={r.notes} onChange={set('notes')} /></Field>
      </div>
    </Section>
  );
}
